import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    UniqueIdentifier
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// evertel
import {
    Row, Card, CardBody, Button, Icon, Text,
    Modal, ModalHeader, ModalBody, ModalFooter, useUI
} from '@evertel/web/ui'
import { APIDataDocumentSchemaCategory } from '@evertel/types'

interface ReorderCategoriesModalProps {
    visible: boolean
    categories: APIDataDocumentSchemaCategory[]
    onSave: (categoryIds: number[]) => Promise<void>
    onClose: () => void
}

const ReorderCategoriesModal: React.FC<ReorderCategoriesModalProps> = observer(({
    visible,
    categories,
    onSave,
    onClose
}) => {

    const { addToast } = useUI()

    const [items, setItems] = useState<APIDataDocumentSchemaCategory[]>([])
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
    const [isSaving, setIsSaving] = useState(false)

    // dragging only touches local state — nothing persists until Save order
    useEffect(() => {
        if (!visible) return

        setItems([...categories])
    }, [visible, categories])

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id)
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        setActiveId(null)

        if (over && active.id !== over.id) {
            const oldIndex = items.findIndex(c => c.id === active.id)
            const newIndex = items.findIndex(c => c.id === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                setItems(arrayMove(items, oldIndex, newIndex))
            }
        }
    }

    const sortAlphabetically = () => {
        setItems([...items].sort((a, b) => (a.name as string).localeCompare(b.name as string)))
    }

    const saveOrder = async () => {
        setIsSaving(true)

        try {
            await onSave(items.map(c => c.id as number))
            onClose()
        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Oops!',
                message: error.message || error
            })
        }

        setIsSaving(false)
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            size="md"
            alignment="center">
            <ModalHeader title="Reorder agency categories" />
            <ModalBody>
                <Text
                    tag="p"
                    color="muted">
                    Drag the categories and drop them where you want them. This reorganization will be visible to all members of the agency.
                </Text>
                <div
                    className="visually-hidden"
                    id="reorder-categories-instructions">
                    Drag items to reorder the agency&apos;s categories.
                </div>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}>
                    <SortableContext
                        items={items.map(c => c.id as number)}
                        strategy={verticalListSortingStrategy}>
                        {items.map((category, index) => (
                            <SortableCategory
                                key={category.id}
                                category={category}
                                index={index}
                                activeId={activeId}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </ModalBody>
            <ModalFooter align="right">
                <Button
                    color="neutral"
                    className="me-2"
                    onClick={sortAlphabetically}>
                    Sort alphabetically
                </Button>
                <Button
                    color="primary"
                    loading={isSaving}
                    disabled={!items.length}
                    onClick={saveOrder}>
                    Save order
                </Button>
            </ModalFooter>
        </Modal>
    )
})

interface SortableCategoryProps {
    category: APIDataDocumentSchemaCategory
    index: number
    activeId: UniqueIdentifier | null
}

const SortableCategory: React.FC<SortableCategoryProps> = ({
    category,
    index,
    activeId
}) => {

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: category.id as number })

    // Only apply transform/transition during active drag
    // This prevents the jump-back-then-animate issue when drag ends
    const style = {
        transform: activeId ? CSS.Transform.toString(transform) : undefined,
        transition: activeId ? transition : undefined,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab'
    }

    // the whole card is the drag activator (no separate handle), so it also carries dnd-kit's
    // role/tabIndex/keyboard attributes. The grip icon is purely a visual affordance
    return (
        <Card
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            style={style}
            className="mb-2 user-select-none"
            aria-label={`Category ${index + 1}: ${category.name}. Drag to reorder`}
            aria-describedby={[attributes['aria-describedby'], 'reorder-categories-instructions'].filter(Boolean).join(' ')}
        >
            <CardBody className="p-2">
                <Row valign="center">
                    <Icon
                        name="grip-dots-vertical"
                        color="muted"
                        className="me-2"
                        aria-hidden="true"
                    />
                    <Text bold>
                        {category.name}
                    </Text>
                </Row>
            </CardBody>
        </Card>
    )
}

export { ReorderCategoriesModal }
